import React, { useEffect, useMemo, useState } from 'react'

interface Player { id: string; name: string }
interface Tournament { id: string; name: string; memberships?: Array<{ playerId: string; level?: string }>; }

export default function PlayerCancellationModal({
  tournament,
  players,
  isOpen,
  onClose,
  onSuccess,
  editingId: externalEditingId,
  prefillPlayerId,
}: {
  tournament: Tournament
  players: Player[]
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  editingId?: string
  prefillPlayerId?: string
}) {
  const participantIds = useMemo(() => new Set((tournament.memberships || []).map(m => m.playerId)), [tournament])
  const [playerId, setPlayerId] = useState('')
  const [mode, setMode] = useState<'FULL'|'PARTIAL'>('FULL')
  const [amount, setAmount] = useState<string>('')
  const [reason, setReason] = useState<'NO_SHOW'|'DECLINE'>('NO_SHOW')
  const [comment, setComment] = useState('')
  const [docDate, setDocDate] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string>('')
  const [existing, setExisting] = useState<Array<{ id: string; date: string; amount: number }>>([])
  const [editingId, setEditingId] = useState<string>('')
  const [level, setLevel] = useState<string>('')
  const toYmd = (val: string | Date) => {
    const d = new Date(val)
    if (isNaN(d.getTime())) return ''
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${dd}`
  }

  useEffect(() => {
    if (isOpen) {
      setPlayerId(prefillPlayerId || '')
      setMode('FULL')
      setAmount('')
      setReason('NO_SHOW')
      setComment('')
      setDocDate('')
      setLevel('')
      setError('')
      setEditingId(externalEditingId || '')
      // загрузим существующие корректировки по игроку
      const pid = prefillPlayerId || playerId
      if (pid) void loadExisting(pid)
      // если пришёл editingId извне — подгрузим сам документ и заполним поля формы
      const loadSelected = async () => {
        if (!externalEditingId) return
        try {
          const r = await fetch(`/api/player-cancellations/${externalEditingId}`)
          if (!r.ok) return
          const c = await r.json()
          if (c?.playerId) setPlayerId(c.playerId)
          const amt = Number(c?.amount || 0)
          setMode((c?.mode as any) || (amt > 0 ? 'PARTIAL' : 'FULL'))
          if (amt > 0) setAmount(String(amt))
          if (c?.reason) setReason(c.reason)
          if (c?.comment) setComment(c.comment)
          if (c?.createdAt) setDocDate(toYmd(c.createdAt))
          if (c?.level) setLevel(String(c.level))
        } catch {}
      }
      void loadSelected()
    }
  }, [isOpen, externalEditingId, prefillPlayerId])

  const playerLevels = useMemo(() => {
    const m = tournament.memberships || []
    return m.filter(x => x.playerId === playerId && x.level)
      .map(x => String(x.level))
  }, [playerId, tournament])

  useEffect(() => {
    if (playerLevels.length === 1) setLevel(playerLevels[0])
    else setLevel('')
  }, [playerLevels.join(',')])
  const loadExisting = async (pId: string) => {
    try {
      const params = new URLSearchParams({ tournamentId: tournament.id })
      const res = await fetch(`/api/reports/player-settlements-movements/${pId}/details?${params.toString()}`)
      const data = await res.json()
      let arr = Array.isArray(data?.items) ? data.items : []
      // Фолбэк: если по турниру пусто, пробуем без фильтра турнира (исторические записи без tournamentId)
      if (!arr.length) {
        const res2 = await fetch(`/api/reports/player-settlements-movements/${pId}/details`)
        const data2 = await res2.json()
        arr = Array.isArray(data2?.items) ? data2.items : []
      }
      const items = arr.filter((it: any) => it.type === 'ADJUSTMENT').map((it: any) => ({ id: it.id, date: it.date, amount: Number(it.amount || 0) }))
      setExisting(items)
    } catch { setExisting([]) }
  }


  const participants = players.filter(p => participantIds.has(p.id))

  const submit = async () => {
    try {
      setSaving(true)
      setError('')
      if (!level) { setError('Выберите уровень регистрации'); setSaving(false); return }
      const body: any = { playerId, tournamentId: tournament.id, mode, comment, reason, date: docDate || undefined, level }
      if (mode === 'PARTIAL') body.amount = Number(amount || 0)
      // Если есть хоть одна корректировка — обновляем выбранную или первую
      const effectiveId = editingId || (existing.length > 0 ? existing[0].id : '')
      const url = effectiveId ? `/api/player-cancellations/${effectiveId}` : '/api/player-cancellations'
      const method = effectiveId ? 'PUT' : 'POST'
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      const data = await res.json()
      if (!res.ok) throw new Error(data?.error || 'Ошибка сохранения')
      onSuccess()
      onClose()
    } catch (e:any) {
      const msg = String(e.message || 'Ошибка')
      // Если нет задолженности и есть корректировки — требуем выбрать запись (кроме случая единственной)
      if (/Нет задолженности/i.test(msg) && existing.length > 0) {
        if (existing.length === 1) {
          const first = existing[0]
          setEditingId(first.id)
          setMode('PARTIAL')
          if (!amount) setAmount(String(first.amount))
          if (!docDate) setDocDate(String(first.date).slice(0,10))
          setError('Остаток долга = 0. Включён режим редактирования найденной корректировки.')
        } else if (!editingId) {
          setError('Остаток долга = 0. Выберите корректировку в списке ниже и повторите сохранение.')
          return
        } else {
          setError(msg)
        }
      } else {
        setError(msg)
      }
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold">Отмена участия — {tournament.name}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>
        <div className="p-4 space-y-3">
          {error && <div className="text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded text-sm">{error}</div>}
          <div>
            <label className="block text-sm mb-1">Игрок</label>
            <select value={playerId} onChange={async e=> { const v = e.target.value; setPlayerId(v); if (v) await loadExisting(v) }} className="w-full border rounded px-3 py-2 text-sm">
              <option value="">— выберите игрока —</option>
              {participants.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm mb-1">Уровень</label>
            <select value={level} onChange={e=> setLevel(e.target.value)} className="w-full border rounded px-3 py-2 text-sm">
              <option value="">— выберите уровень —</option>
              {(playerLevels || []).map(lv => (
                <option key={lv} value={lv}>{lv}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm mb-1">Режим</label>
              <select value={mode} onChange={e=> setMode(e.target.value as any)} className="w-full border rounded px-3 py-2 text-sm">
                <option value="FULL">Полностью без компенсации</option>
                <option value="PARTIAL">Частично (с компенсацией)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1">Причина</label>
              <select value={reason} onChange={e=> setReason(e.target.value as any)} className="w-full border rounded px-3 py-2 text-sm">
                <option value="NO_SHOW">Неявка</option>
                <option value="DECLINE">Отказ</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm mb-1">Дата документа</label>
              <input type="date" value={docDate} onChange={e=> setDocDate(e.target.value)} className="w-full border rounded px-3 py-2 text-sm" />
            </div>
          </div>
          {mode === 'PARTIAL' && (
            <div>
              <label className="block text-sm mb-1">Сумма уменьшения долга</label>
              <input type="number" step="0.01" value={amount} onChange={e=> setAmount(e.target.value)} className="w-full border rounded px-3 py-2 text-sm" placeholder="0.00" />
            </div>
          )}
          <div>
            <label className="block text-sm mb-1">Комментарий</label>
            <textarea value={comment} onChange={e=> setComment(e.target.value)} className="w-full border rounded px-3 py-2 text-sm" rows={3} placeholder="укажите детали" />
          </div>
          {/* блок со списком уже созданных отказов скрыт по требованию */}
        </div>
        <div className="px-4 py-3 border-t flex gap-2 justify-end">
          <button onClick={onClose} className="px-3 py-2 text-sm border rounded">Отмена</button>
          <button disabled={saving || !playerId || !level || !comment} onClick={submit} className="px-3 py-2 text-sm bg-blue-600 text-white rounded disabled:opacity-50">Сохранить</button>
        </div>
      </div>
    </div>
  )
}


